import { useCallback, useEffect, useState } from "react";
import cancelIcon from "../../assets/cancelIcon.svg";
import axios from "axios";
import { millisecondsToMinutes, millisecondsToSeconds } from "date-fns";
import LoadingComponent from "../../components/Loading";
import debounce from "../../utils/Debounce";
import axiosCMS from "../../libs/axios/axiosCMS";

const AvgSessionLength = ({ isOpen, onClose, selectedFilters }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const rowsPerPage = 5;

  const indexOfLastData = currentPage * rowsPerPage;
  const indexOfFirstData = indexOfLastData - rowsPerPage;

  /**
   * Fetches average session length data based on selected filters.
   * @param {Object} filters - The filters to apply when fetching data.
   */
  const getData = useCallback(debounce(async (filters) => {
    setIsLoading(true)
    try {
      const URL = 'user-engagement/session_length_detail';
      const response = await axiosCMS.post(URL, filters);
      const result = response.data;
      setData(result.data);
    } catch (error) {
      console.error('Error fetching data: ', error);
    }
    setIsLoading(false);
  }, 1500))

  useEffect(() => {
    if (isOpen) {
      getData(selectedFilters);
      setCurrentPage(1);
    }
  }, [isOpen]);

  const dataPaginator = () => {
    const paginated = data.slice(indexOfFirstData, indexOfLastData);
    setPaginatedData(paginated);
  }

  useEffect(() => {
    dataPaginator();
  }, [currentPage, data]);

  const paginate = (direction) => {
    if (direction === 'next' && currentPage < Math.ceil(data.length / rowsPerPage)) {
      setCurrentPage(currentPage + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }

  if (!isOpen) return null;

  return (
    <div className={`ue-avg-session-length ${isOpen ? "open" : ""}`}>
      <div className="ue-avg-session-length__content">
        <div className="ue-avg-session-length__header">
          <h1 className="ue-avg-session-length__title">Average Session Length</h1>
          <button className="ue-avg-session-length__btn-close" onClick={onClose}>
            <img src={cancelIcon} alt="cancel icon" />
          </button>
        </div>
        <div className="ue-avg-session-length__separator" />
        <div className="ue-avg-session-length__section">
          <div className="ue-avg-session-length__box">
            <table className="ue-avg-session-length__table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Average Duration</th>
                  <th>Total Session</th>
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? <tr>
                    <td colSpan={3}><LoadingComponent message={'Loading data...'} /></td>
                  </tr>
                  : paginatedData.length !== 0
                    ? paginatedData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.email}</td>
                        <td>{item.role?.role_name ?? '-'}</td>
                        <td>{item.avg_duration >= 60000 ? millisecondsToMinutes(item.avg_duration) : millisecondsToSeconds(item.avg_duration)}<span> {item.avg_duration > 60000 ? 'minutes' : 'seconds'}</span></td>
                        <td>{item.total_sessions}</td>
                      </tr>
                    ))
                    : <tr>
                      <td colSpan={3} className="text-center">No data found</td>
                    </tr>
                }
              </tbody>
            </table>
            <div className="ue-avg-session-length__pagination">
              <button onClick={() => paginate('prev')} disabled={currentPage === 1}>Previous</button>
              <button onClick={() => paginate('next')} disabled={currentPage === (Math.ceil(data.length / rowsPerPage) >= 1 ? Math.ceil(data.length / rowsPerPage) : 1)}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
}

export default AvgSessionLength;